import { Options, Splide } from '@splidejs/splide'

import HttpService from '../Service/HttpService'
import { isTabletWideDown, mediaQueries } from '../Utility/Breakpoint'
import { getElementChild, getElements } from '../Utility/Dom'
import {
  AbstractTeaserList,
  BaseTeaserItem,
  HydraResponse,
} from './AbstractTeaserList'

const SLIDER_BUFFER_ZONE = 3

interface ArticleItem extends BaseTeaserItem {
  publishDate?: null | string
  tags?: Tag[]
}

interface Tag {
  title: string
  uid: number
}

class ArticleList extends AbstractTeaserList<ArticleItem> {
  private hasMoreSliderItems = false

  private readonly layout: number
  private slider: null | Splide = null
  private readonly sliderElement: HTMLElement | null
  private sliderMounted = false
  constructor(container: HTMLElement) {
    const apiUrlBase = container.dataset.apiUrl ?? ''
    super(container, {
      apiUrl: apiUrlBase === '' ? '' : `${apiUrlBase.replace(/\/$/, '')}/articles`,
      containerSelector: 'article-list',
      itemsPerPage: 6,
      listSelector: 'article-list-items',
      templateSelector: 'article-item-template',
    })

    this.layout = parseInt(container.dataset.layout ?? '0', 10)
    this.sliderElement = getElementChild(this.container, 'article-slider')
  }

  public init(): void {
    if (this.layout === 0) {
      this.initSlider()
    } else if (this.layout === 1) {
      this.initResponsiveSlider()
    }
  }

  protected createItem(item: ArticleItem): HTMLElement | null {
    if (this.template === null) {
      return null
    }

    const fragment = this.template.content.cloneNode(true) as DocumentFragment
    const element = fragment.firstElementChild as HTMLElement

    if (element === null) {
      return null
    }

    const badge = getElementChild(element, 'article-badge')
    const date = getElementChild(element, 'article-date')
    const link = getElementChild(element, 'article-link') as HTMLAnchorElement | null
    const picture = getElementChild(element, 'article-picture')
    const tags = getElementChild(element, 'article-tags')
    const text = getElementChild(element, 'article-text')
    const title = getElementChild(element, 'article-title')

    if (picture !== null) {
      this.updatePictureSources(picture, item)
    }

    if (badge !== null && item.category) {
      badge.textContent = item.category.title

      if (item.category.color) {
        badge.classList.add('bg-' + item.category.color)
      }
    } else {
      badge?.remove()
    }

    if (date !== null && item.publishDate) {
      date.textContent = this.formatDate(item.publishDate)
    } else {
      date?.remove()
    }

    if (link !== null) {
      link.href = item.detailLink || item.slug
    }

    if (title !== null) {
      title.textContent = item.teaserTitle || item.title
    }

    if (text !== null) {
      text.textContent = this.truncateText(item?.teaserText || '', 120)
    }

    if (tags !== null && item.tags && item.tags.length > 0) {
      item.tags.forEach((tag: Tag): void => {
        const li = document.createElement('li')
        li.className = 'tag'
        li.textContent = tag.title
        tags.appendChild(li)
      })
    } else {
      tags?.remove()
    }

    if (this.layout === 0 || (this.layout === 1 && this.sliderMounted)) {
      element.classList.add('splide__slide')
    }

    return element
  }

  protected async loadItems(replaceItems: boolean): Promise<void> {
    return super.loadItems(replaceItems)
  }

  private addSlideClasses(): void {
    if (this.list === null) {
      return
    }

    const items = this.list.children

    for (let i = 0; i < items.length; i++) {
      items[i].classList.add('splide__slide')
    }
  }

  private addSliderLazyLoad(): void {
    if (this.slider === null) {
      return
    }

    this.slider.on('moved', (newIndex: number): void => {
      if (this.slider === null || this.isLoading === true || this.hasMoreSliderItems === false) {
        return
      }

      const totalSlides = this.slider.Components.Elements.list.childElementCount
      const perPage = this.slider.options.perPage ?? 3
      const remainingSlides = totalSlides - newIndex - perPage

      if (remainingSlides <= SLIDER_BUFFER_ZONE) {
        this.loadSliderItems()
      }
    })

    this.slider.on('arrows:updated', (_prev: HTMLButtonElement, next: HTMLButtonElement): void => {
      if (this.hasMoreSliderItems === true && next.disabled === true) {
        requestAnimationFrame((): void => {
          next.disabled = false
        })
      }
    })
  }

  private addSliderSkeletonSlides(): void {
    if (this.slider === null || this.skeleton === null) {
      return
    }

    const skeletonItem = this.skeleton.querySelector('.skeleton__item')

    if (skeletonItem === null) {
      return
    }

    for (let i = 0; i < this.itemsPerPage; i++) {
      const clone = skeletonItem.cloneNode(true) as HTMLElement
      clone.dataset.skeletonSlide = 'true'
      this.slider?.add(clone)
    }
  }

  private buildSliderApiUrl(): string {
    const params = new URLSearchParams()

    if (this.isRecordMode) {
      this.recordSlice(this.currentPage).forEach((uid: number): void => {
        params.append('uid[]', uid.toString())
      })

      return `${this.apiUrl}?${params.toString()}`
    }

    params.set('page', this.currentPage.toString())
    params.set('itemsPerPage', this.itemsPerPage.toString())

    if (this.tagId !== null) {
      params.set('tags', this.tagId.toString())

      return `${this.apiUrl}?${params.toString()}`
    }

    this.pageIds.forEach((pid: number): void => {
      params.append('pid[]', pid.toString())
    })

    return `${this.apiUrl}?${params.toString()}`
  }

  private formatDate(dateString: string): string {
    const date = new Date(dateString)
    const months = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez']

    return `${date.getDate()}. ${months[date.getMonth()]}, ${date.getFullYear()}`
  }

  private getSliderOptions(): Options {
    return {
      arrows: true,
      /* eslint-disable perfectionist/sort-objects -- splide requires descending breakpoint order */
      breakpoints: {
        1024: {
          perMove: 1,
          perPage: 2,
        },
        768: {
          pagination: false,
          perMove: 1,
          perPage: 2,
        },
        575: {
          focus: 'center',
          gap: '1rem',
          pagination: false,
          perMove: 1,
          perPage: 1.1,
        },
      },
      /* eslint-enable perfectionist/sort-objects */
      gap: '1.25rem',
      perMove: 1,
      perPage: 3,
      type: 'slide',
    }
  }

  private initResponsiveSlider(): void {
    if (this.sliderElement === null) {
      return
    }

    const breakpointQuery = mediaQueries.tabletWide.down
    const sliderOptions = this.getSliderOptions()

    this.slider = new Splide(this.sliderElement, sliderOptions)

    const toggleSlider = (isActive: boolean): void => {
      if (this.slider === null) {
        return
      }

      if (isActive) {
        if (!this.sliderMounted) {
          this.addSlideClasses()
          this.slider.mount()
          this.sliderMounted = true
          this.hasMoreSliderItems = this.isRecordMode
            ? this.recordIds.length > this.slider.length
            : this.slider.length >= this.itemsPerPage
          this.addSliderLazyLoad()
          this.slider.on('resized', (): void => this.updateControlsVisibility())
          this.updateControlsVisibility()
        }
      } else {
        if (this.sliderMounted) {
          this.slider.destroy(true)
          this.sliderMounted = false
          this.removeSlideClasses()
        }
      }
    }

    toggleSlider(isTabletWideDown())

    breakpointQuery.addEventListener('change', (event): void => {
      toggleSlider(event.matches)
    })
  }

  private initSlider(): void {
    if (this.sliderElement === null) {
      return
    }

    const sliderOptions = this.getSliderOptions()
    this.slider = new Splide(this.sliderElement, sliderOptions)
    this.slider.on('resized', (): void => this.updateControlsVisibility())
    this.slider.mount()
    this.sliderMounted = true
    this.hasMoreSliderItems = this.isRecordMode
      ? this.recordIds.length > this.slider.length
      : this.slider.length >= this.itemsPerPage
    this.addSliderLazyLoad()
    this.updateControlsVisibility()
  }

  private async loadSliderItems(): Promise<void> {
    if (this.slider === null || this.isLoading === true || this.apiUrl === '') {
      return
    }

    this.isLoading = true
    this.currentPage++
    this.addSliderSkeletonSlides()

    const url = this.buildSliderApiUrl()

    try {
      const response = await HttpService.get(url) as HydraResponse<ArticleItem>

      this.removeSliderSkeletonSlides()

      const items = this.isRecordMode
        ? this.orderBySelection(response['hydra:member'])
        : response['hydra:member']

      items.forEach((item: ArticleItem): void => {
        const element = this.createItem(item)

        if (element !== null) {
          this.slider?.add(element)
        }
      })

      this.hasMoreSliderItems = this.isRecordMode
        ? this.currentPage * this.itemsPerPage < this.recordIds.length
        : response['hydra:view']?.['hydra:next'] !== undefined
    } catch (error) {
      console.error('Error loading slider items:', error)
      this.removeSliderSkeletonSlides()
    } finally {
      this.isLoading = false
    }
  }

  private removeSlideClasses(): void {
    if (this.list === null) {
      return
    }

    const items = this.list.children

    for (let i = 0; i < items.length; i++) {
      items[i].classList.remove('splide__slide')
    }
  }

  private removeSliderSkeletonSlides(): void {
    if (this.slider === null) {
      return
    }

    const list = this.slider.Components.Elements.list
    const skeletonSlides = list.querySelectorAll('[data-skeleton-slide="true"]')

    skeletonSlides.forEach((el: Element): void => {
      const slide = el.closest('.splide__slide') ?? el
      slide.remove()
    })

    this.slider.refresh()
  }

  private updateControlsVisibility(): void {
    if (this.slider === null || this.sliderElement === null) {
      return
    }

    const controls = this.sliderElement.querySelector<HTMLElement>('.splide__controls')

    if (controls === null) {
      return
    }

    const perPage = Math.ceil(this.slider.options.perPage ?? 3)
    controls.style.display = this.slider.length <= perPage ? 'none' : ''
  }
}

const containers = getElements('article-list')

containers.forEach((container: HTMLElement): void => {
  const articleList = new ArticleList(container)
  articleList.init()
})
